import React, { createContext, useContext, useState, ReactNode } from "react";

// Librería para el manejo de idiomas y locale.
import { IntlProvider } from "react-intl";
import EnglishMessages from "../lang/en-US.json";
import SpanishMessages from "../lang/es-ES.json";
import PortugueseMessages from "../lang/pt-BR.json";
import {
	LoginContext,
	LoginContextValues,
	MultistepContext,
	MultistepContextValues,
} from "./context";

interface LangProviderProps {
	children: ReactNode;
}

export const languages = ["es-ES", "en-US", "pt-BR"];
// First we create a Lang Context with the language and translations for the App.
const LangContext = createContext<{
	onLanguageChange: (language: string) => void;
	locale: string;
} | null>(null);

// Creating the provider to obtain the necessary functions and wrap the App.
const LangProvider: React.FC<LangProviderProps> = ({ children }) => {
	// We get the multistep information
	const { onChangeStep, reservationMade, activeStep } = useContext(
		MultistepContext
	) as MultistepContextValues;
	const { isCalledFromProfile } = useContext(
		LoginContext
	) as LoginContextValues;

	//We store in 'messages' the record with the name and the corresponding text in the selected language
	const [messages, setMessages] = useState<Record<string, string>>(
		SpanishMessages as Record<string, string>
	);

	//We store in 'locale' the code of the selected language
	const [locale, setLocale] = useState("es-ES");

	// If the language changes, we redirect to the first step of the flow and reset the reservation state.
	// We also store in 'messages' the text of the corresponding file.
	const onLanguageChange = (languaje: string) => {
		if (
			(!reservationMade && activeStep > 0) ||
			(activeStep === -2 && !isCalledFromProfile)
		)
			onChangeStep(1);

		switch (languaje) {
			case "en-US":
				setMessages(EnglishMessages as Record<string, string>);
				break;
			case "pt-BR":
				setMessages(PortugueseMessages as Record<string, string>);
				break;
			default:
				setMessages(SpanishMessages as Record<string, string>);
		}
		setLocale(languaje);
	};

	/* We return the lang context created and an IntlProvided, from react-intl 
  library and set the messages and locale name. */
	return (
		<LangContext.Provider value={{ onLanguageChange, locale }}>
			<IntlProvider locale={locale} messages={messages}>
				{children}
			</IntlProvider>
		</LangContext.Provider>
	);
};

export { LangProvider, LangContext };
